import { useCallback, useEffect, useMemo, useState } from 'react';
import NotificationMessagingSection from '../../components/NotificationMessagingSection';
import DatedRecordsReport from '../../components/DatedRecordsReport';
import JalaliDateRangeFilter from '../../components/JalaliDateRangeFilter';
import { HomeNavButton } from '../../components/NavButtons';
import ParentNotificationModal from '../../components/ParentNotificationModal';
import {
  filterParentNotifications,
  filterSentNotificationsBySender,
  getNotificationSenderTitle,
  getReceivedNotifications,
  getSentNotifications,
} from '../../services/notificationService';
import { getStudentClasses } from '../../services/classService';
import { getStudentAttendance } from '../../services/parentService';
import { getStudentDisciplines, getStudentLevels } from '../../services/datedRecordService';
import {
  flattenClassSubjects,
  formatClassSubjectOptionLabel,
  getClassTitle,
  getSubjectTitle,
} from '../../utils/classSubjectUtils';
import { formatDateRangeLabel, formatJalaliDate, getDefaultDateRange } from '../../utils/jalaliDate';

const tabs = [
  { id: 'attendance', label: 'حضور و غیاب' },
  { id: 'levels', label: 'ارزشیابی ها' },
  { id: 'disciplines', label: 'شایستگی های عمومی' },
  { id: 'inbox', label: 'پیام های دریافتی' },
  { id: 'compose', label: 'ارسال پیام' },
  { id: 'sent', label: 'پیام های ارسالی' },
];

const statusConfig = {
  'حاضر': { color: '#1FC24E', label: 'حضور' },
  'حضور با تاخیر': { color: '#FF9238', label: 'حضور با تاخیر' },
  'غایب': { color: '#E02727', label: 'غیبت' },
};

function getStatusColor(status) {
  return statusConfig[status]?.color || '#54B0F8';
}

function formatNotificationDate(value) {
  if (!value) {
    return 'بدون تاریخ';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'بدون تاریخ';
  }

  return new Intl.DateTimeFormat('fa-IR', {
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    month: 'long',
  }).format(date);
}

function buildAttendanceStats(statusCounts = []) {
  const knownStatuses = ['حاضر', 'حضور با تاخیر', 'غایب'];

  return knownStatuses.map((status) => ({
    status,
    count: statusCounts.find((item) => item.status === status)?.count || 0,
  }));
}

function ParentsPage() {
  const [activeTab, setActiveTab] = useState('attendance');
  const [attendance, setAttendance] = useState({
    startDate: null,
    endDate: null,
    totalRecords: 0,
    statusCounts: [],
    records: [],
  });
  const [classSubjects, setClassSubjects] = useState([]);
  const [selectedClassSubjectId, setSelectedClassSubjectId] = useState('');
  const [dateRange, setDateRange] = useState(() => getDefaultDateRange(30));
  const [isAttendanceLoading, setIsAttendanceLoading] = useState(true);
  const [attendanceError, setAttendanceError] = useState('');
  const [parentNotifications, setParentNotifications] = useState([]);
  const [isNotificationsLoading, setIsNotificationsLoading] = useState(true);
  const [notificationsError, setNotificationsError] = useState('');
  const [selectedNotificationId, setSelectedNotificationId] = useState(null);
  const [sentCount, setSentCount] = useState(0);

  const handleNotificationSeen = useCallback((seenNotification) => {
    setParentNotifications((currentNotifications) =>
      currentNotifications.map((notification) =>
        notification.id === seenNotification.id ? seenNotification : notification
      )
    );
  }, []);

  const unreadParentNotificationsCount = useMemo(
    () => parentNotifications.filter((notification) => !notification.isSeen).length,
    [parentNotifications]
  );

  const showSubjectSelector = activeTab === 'attendance';

  useEffect(() => {
    let isMounted = true;

    async function loadClassSubjects() {
      try {
        const classItems = await getStudentClasses();

        if (isMounted) {
          setClassSubjects(flattenClassSubjects(classItems));
        }
      } catch {
        if (isMounted) {
          setClassSubjects([]);
        }
      }
    }

    loadClassSubjects();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    async function loadAttendance() {
      setIsAttendanceLoading(true);
      setAttendanceError('');

      try {
        const attendanceData = await getStudentAttendance({
          classSubjectId: selectedClassSubjectId,
          startDate: dateRange.startDate || undefined,
          endDate: dateRange.endDate || undefined,
        });

        if (isMounted) {
          setAttendance(attendanceData);
        }
      } catch (error) {
        if (isMounted) {
          setAttendanceError(error.message || 'امکان دریافت اطلاعات حضور و غیاب وجود ندارد.');
        }
      } finally {
        if (isMounted) {
          setIsAttendanceLoading(false);
        }
      }
    }

    loadAttendance();

    return () => {
      isMounted = false;
    };
  }, [selectedClassSubjectId, dateRange.startDate, dateRange.endDate]);

  useEffect(() => {
    let isMounted = true;

    async function loadParentNotifications() {
      setIsNotificationsLoading(true);
      setNotificationsError('');

      try {
        const receivedNotifications = await getReceivedNotifications();

        if (isMounted) {
          setParentNotifications(filterParentNotifications(receivedNotifications));
        }
      } catch (error) {
        if (isMounted) {
          setNotificationsError(error.message || 'امکان دریافت اطلاعیه‌های والدین وجود ندارد.');
        }
      } finally {
        if (isMounted) {
          setIsNotificationsLoading(false);
        }
      }
    }

    async function loadSentCount() {
      try {
        const sentNotifications = await getSentNotifications();

        if (isMounted) {
          setSentCount(filterSentNotificationsBySender(sentNotifications, 'parent').length);
        }
      } catch {
        // Badge stays optional if sent list fails to load.
      }
    }

    loadParentNotifications();
    loadSentCount();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <p
            style={{
              background: 'linear-gradient(90deg, #7FDE86 0%, #1FC24E 100%)',
              boxShadow: '4px 5px 11px rgba(0, 0, 0, 0.07)',
            }}
            className="rounded-[10px] px-6 py-1 text-[15px] text-white"
          >
            پنجره اولیا
          </p>

          <img className="h-[50px]" src="/images/parent.webp" alt="" />
        </div>

        <HomeNavButton />
      </div>

      <div className="grid grid-cols-3 gap-1 rounded-[12px] bg-slate-100 p-1">
        {tabs.map((tab) => {
          const isActive = tab.id === activeTab;
          const badgeCount =
            tab.id === 'inbox'
              ? unreadParentNotificationsCount
              : tab.id === 'sent'
                ? sentCount
                : 0;

          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center justify-center gap-1 rounded-[9px] px-1 py-2.5 text-[11px] font-bold transition ${
                isActive
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-slate-500'
              }`}
            >
              <span className="text-center leading-4">{tab.label}</span>
              {badgeCount > 0 && (
                <span
                  className={`inline-flex min-w-[16px] items-center justify-center rounded-full px-1 text-[9px] tabular-nums ${
                    isActive ? 'bg-[#1FC24E]/12 text-[#1FC24E]' : 'bg-slate-200 text-slate-500'
                  }`}
                >
                  {badgeCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {showSubjectSelector && classSubjects.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          <button
            type="button"
            onClick={() => setSelectedClassSubjectId('')}
            className={`shrink-0 rounded-[8px] px-3 py-1.5 text-[11px] transition-colors ${
              !selectedClassSubjectId
                ? 'bg-[#1FC24E] font-medium text-white'
                : 'border border-slate-200 bg-white text-slate-600'
            }`}
          >
            همه درس‌ها
          </button>
          {classSubjects.map((classSubject) => {
            const isActive = selectedClassSubjectId === classSubject.id;

            return (
              <button
                key={classSubject.id}
                type="button"
                onClick={() => setSelectedClassSubjectId(classSubject.id)}
                className={`shrink-0 rounded-[8px] px-3 py-1.5 text-[11px] transition-colors ${
                  isActive
                    ? 'bg-[#1FC24E] font-medium text-white'
                    : 'border border-slate-200 bg-white text-slate-600'
                }`}
              >
                {formatClassSubjectOptionLabel(classSubject)}
              </button>
            );
          })}
        </div>
      )}

      {activeTab === 'attendance' && (
        <div className="rounded-[12px] border border-slate-100 bg-white p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[14px] font-medium text-slate-800">حضور و غیاب</p>
              <p className="mt-1 text-[12px] text-slate-500">
                {formatDateRangeLabel(dateRange.startDate, dateRange.endDate)}
              </p>
            </div>

            <span className="rounded-full bg-[#F8F8F8] px-3 py-1 text-[11px] text-slate-500">
              {attendance.totalRecords || 0} رکورد
            </span>
          </div>

          <JalaliDateRangeFilter
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={setDateRange}
          />

          {attendanceError ? (
            <MessageCard message={attendanceError} tone="error" />
          ) : isAttendanceLoading ? (
            <MessageCard message="در حال دریافت گزارش حضور و غیاب..." />
          ) : (
            <>
              <div className="mt-4 grid grid-cols-3 gap-2">
                {buildAttendanceStats(attendance.statusCounts).map((item) => (
                  <div
                    key={item.status}
                    className="rounded-[8px] px-2 py-2 text-center text-white"
                    style={{ background: getStatusColor(item.status) }}
                  >
                    <p className="text-[10px] font-light">
                      {statusConfig[item.status]?.label || item.status}
                    </p>
                    <p className="mt-1 text-[17px] font-semibold">{item.count}</p>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex flex-col gap-2">
                {(attendance.records || []).length === 0 ? (
                  <MessageCard message="برای این بازه رکوردی ثبت نشده است." />
                ) : (
                  attendance.records.map((record) => (
                    <div key={record.id} className="rounded-[10px] border border-slate-100 bg-[#FAFAFA] p-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <span
                            className="h-[10px] w-[10px] rounded-full"
                            style={{ background: getStatusColor(record.status) }}
                          />
                          <div>
                            <p className="text-[13px] font-medium text-slate-800">
                              {record.status || 'نامشخص'}
                            </p>
                            <p className="text-[11px] text-slate-500">
                              {formatJalaliDate(record.date)}
                            </p>
                          </div>
                        </div>

                        <span className="rounded-[6px] bg-white px-2 py-1 text-[10px] text-slate-500">
                          {[getSubjectTitle(record), getClassTitle(record, '')].filter(Boolean).join(' · ') || 'کلاس'}
                        </span>
                      </div>

                      {record.description ? (
                        <div className="mt-2 grid grid-cols-1 gap-2 text-[11px] text-slate-600">
                          <p className="rounded-[6px] bg-white px-2 py-2 leading-5">
                            توضیحات: {record.description}
                          </p>
                        </div>
                      ) : null}
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'levels' && (
        <DatedRecordsReport
          title="ارزشیابی ها"
          subtitle="گزارش ارزشیابی دانش‌آموز"
          fetchReport={getStudentLevels}
          countsKey="levelCounts"
          countValueKey="level"
          countLabelKey="levelLabel"
          valueKey="level"
          labelKey="levelLabel"
          accentClassName="bg-[#1FC24E] text-white"
          showHomeButton={false}
          embedded
          showTeacher={false}
          useJalaliDateRange
        />
      )}

      {activeTab === 'disciplines' && (
        <DatedRecordsReport
          title="شایستگی های عمومی"
          subtitle="گزارش شایستگی های عمومی دانش‌آموز"
          fetchReport={getStudentDisciplines}
          countsKey="disciplineCounts"
          countValueKey="discipline"
          countLabelKey="disciplineLabel"
          valueKey="discipline"
          labelKey="disciplineLabel"
          accentClassName="bg-[#1FC24E] text-white"
          showHomeButton={false}
          embedded
          showTeacher={false}
          useJalaliDateRange
        />
      )}

      {activeTab === 'inbox' && (
        <section className="space-y-2">
          {!isNotificationsLoading && !notificationsError && parentNotifications.length > 0 && (
            <p className="px-0.5 text-[12px] text-slate-500">
              {unreadParentNotificationsCount > 0
                ? `${unreadParentNotificationsCount} پیام خوانده‌نشده از ${parentNotifications.length} پیام`
                : `${parentNotifications.length} پیام`}
            </p>
          )}

          {notificationsError ? (
            <div className="rounded-[12px] border border-slate-100 bg-white px-4 py-6 text-center">
              <p className="text-[13px] text-[#E02727]">{notificationsError}</p>
            </div>
          ) : isNotificationsLoading ? (
            <div className="rounded-[12px] border border-slate-100 bg-white px-4 py-8 text-center text-[12px] text-slate-500">
              در حال دریافت اطلاعیه‌های والدین...
            </div>
          ) : parentNotifications.length === 0 ? (
            <div className="rounded-[12px] border border-slate-100 bg-white px-4 py-8 text-center">
              <p className="text-[14px] font-medium text-slate-800">فعلاً اطلاعیه‌ای ثبت نشده</p>
              <p className="mt-2 text-[12px] text-slate-500">
                پیام‌های مخصوص اولیا اینجا نمایش داده می‌شوند.
              </p>
            </div>
          ) : (
            parentNotifications.map((notification) => {
              const isUnread = !notification.isSeen;

              return (
                <button
                  key={notification.id}
                  type="button"
                  onClick={() => setSelectedNotificationId(notification.id)}
                  className={`block w-full rounded-[12px] border bg-white px-4 py-3.5 text-right transition active:scale-[0.99] ${
                    isUnread
                      ? 'border-[#1FC24E]/30 shadow-[0_2px_10px_rgba(31,194,78,0.08)]'
                      : 'border-slate-100'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span
                      className={`mt-2 h-2 w-2 shrink-0 rounded-full ${
                        isUnread ? 'bg-[#1FC24E]' : 'bg-transparent'
                      }`}
                      aria-hidden={!isUnread}
                    />

                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <p
                          className={`truncate text-[14px] leading-6 text-slate-800 ${
                            isUnread ? 'font-semibold' : 'font-medium'
                          }`}
                        >
                          {notification.title || 'بدون عنوان'}
                        </p>

                        {isUnread && (
                          <span className="shrink-0 text-[10px] font-medium text-[#1FC24E]">جدید</span>
                        )}
                      </div>

                      <p className="mt-1.5 line-clamp-2 text-[13px] leading-6 text-slate-600">
                        {notification.description || 'متنی برای این اطلاعیه ثبت نشده است.'}
                      </p>

                      <p className="mt-2.5 text-[11px] text-slate-400">
                        {getNotificationSenderTitle(notification)}
                        {' · '}
                        {formatNotificationDate(notification.createdAt)}
                        {notification.parentId ? ' · پاسخ' : ''}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </section>
      )}

      {activeTab === 'compose' && (
        <NotificationMessagingSection
          senderRole="parent"
          variant="parent"
          section="compose"
          onSent={() => setSentCount((current) => current + 1)}
        />
      )}

      {activeTab === 'sent' && (
        <NotificationMessagingSection
          senderRole="parent"
          variant="parent"
          section="sent"
          onSentCountChange={setSentCount}
          onOpenConversation={setSelectedNotificationId}
        />
      )}

      <ParentNotificationModal
        notificationId={selectedNotificationId}
        onClose={() => setSelectedNotificationId(null)}
        onSeen={handleNotificationSeen}
      />
    </div>
  );
}

function MessageCard({ message, tone = 'neutral' }) {
  return (
    <div className="mt-3 rounded-[8px] bg-[#F8F8F8] px-4 py-5 text-center">
      <p className={`text-[12px] ${tone === 'error' ? 'font-medium text-[#E02727]' : 'text-slate-500'}`}>
        {message}
      </p>
    </div>
  );
}

export default ParentsPage;
